import languages from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import countries from 'shared/constants/countries';
import {
  generateCustomAttributeTypes,
  getActionOptions,
  getConditionOptions,
  getCustomAttributeInputType,
  getOperatorTypes,
  isACustomAttribute,
  getFileName,
  getDefaultConditions,
  getDefaultActions,
  filterCustomAttributes,
  generateAutomationPayload,
  getStandardAttributeInputType,
  isCustomAttribute,
  generateCustomAttributes,
} from 'dashboard/helper/automationHelper';
import { REENGAGEMENT_COUNT_CONDITION } from 'dashboard/routes/dashboard/settings/automation/constants';
import { mapGetters } from 'vuex';
import boards from '../../api/boards';
import macros from '../../api/macros';

export default {
  computed: {
    ...mapGetters({
      agents: 'agents/getAgents',
      campaigns: 'campaigns/getAllCampaigns',
      contacts: 'contacts/getContacts',
      inboxes: 'inboxes/getInboxes',
      labels: 'labels/getLabels',
      teams: 'teams/getTeams',
      boards: 'boards/getBoards',
      macros: 'macros/getMacros',
      whatsInboxes: 'inboxes/getWhatsAppTemplates',
    }),
    booleanFilterOptions() {
      return [
        {
          id: true,
          name: this.$t('FILTER.ATTRIBUTE_LABELS.TRUE'),
        },
        {
          id: false,
          name: this.$t('FILTER.ATTRIBUTE_LABELS.FALSE'),
        },
      ];
    },

    statusFilterOptions() {
      const statusFilters = this.$t('CHAT_LIST.CHAT_STATUS_FILTER_ITEMS');
      const options = Object.keys(statusFilters).map(status => {
        return {
          id: status,
          name: statusFilters[status].TEXT,
        };
      });

      // Avoid duplicate "All" when translations already include it
      if (!options.some(option => option.id === 'all')) {
        options.push({
          id: 'all',
          name: this.$t('CHAT_LIST.FILTER_ALL'),
        });
      }

      return options;
    },
  },
  methods: {
    getFileName,
    onEventChange() {
      this.automation.conditions = getDefaultConditions(
        this.automation.event_name
      );
      this.automation.actions = getDefaultActions();
    },
    getAttributes(key) {
      return this.automationTypes[key].conditions;
    },
    getInputType(key) {
      const customAttribute = isACustomAttribute(this.allCustomAttributes, key);
      if (customAttribute) {
        return getCustomAttributeInputType(
          customAttribute.attribute_display_type
        );
      }
      const type = this.getAutomationType(key);
      return type.inputType;
    },
    getOperators(key) {
      if (this.mode === 'edit') {
        const customAttribute = isACustomAttribute(
          this.allCustomAttributes,
          key
        );
        if (customAttribute) {
          return getOperatorTypes(customAttribute.attribute_display_type);
        }
      }
      const type = this.getAutomationType(key);
      return type.filterOperators;
    },
    getAutomationType(key) {
      const flag = this.automationTypes[this.automation.event_name].conditions.find(
        condition => condition.key === key
      );
      return this.automationTypes[this.automation.event_name].conditions.find(
        condition => condition.key === key
      );
    },
    getCustomAttributeType(key) {
      const type = this.automationTypes[
        this.automation.event_name
      ].conditions.find(i => i.key === key).customAttributeType;
      return type;
    },
    getConditionDropdownValues(type) {
      const {
        agents,
        allCustomAttributes: customAttributes,
        booleanFilterOptions,
        campaigns,
        contacts,
        inboxes,
        labels,
        statusFilterOptions,
        teams,
        boards,
      } = this;
      
      return getConditionOptions({
        agents,
        booleanFilterOptions,
        campaigns,
        contacts,
        customAttributes,
        inboxes,
        labels,
        statusFilterOptions,
        teams,
        languages,
        countries,
        type,
        boards,
      });
    },
    appendNewCondition() {
      this.automation.conditions.push(
        ...getDefaultConditions(this.automation.event_name)
      );
      const list = this.automation.conditions;
      for (let i = 0; i < list.length - 1; i += 1) {
        const row = list[i];
        if (row && (row.query_operator == null || row.query_operator === '')) {
          this.$set(row, 'query_operator', 'and');
        }
      }
    },
    appendNewAction() {
      this.automation.actions.push(...getDefaultActions());
    },
    removeFilter(index) {
      if (this.automation.conditions.length <= 1) {
        this.showAlert(this.$t('AUTOMATION.CONDITION.DELETE_MESSAGE'));
      } else {
        this.automation.conditions.splice(index, 1);
      }
    },
    removeAction(index) {
      if (this.automation.actions.length <= 1) {
        this.showAlert(this.$t('AUTOMATION.ACTION.DELETE_MESSAGE'));
      } else {
        this.automation.actions.splice(index, 1);
      }
    },
    submitAutomation() {
      this.$v.$touch();
      if (this.$v.$invalid) return;
      const automation = generateAutomationPayload(this.automation);
      this.$emit('saveAutomation', automation, this.mode);
    },
    resetFilter(index, currentCondition) {
      if (currentCondition.attribute_key === 'meeting_present') {
        this.automation.conditions[index].filter_operator = 'is_present';
        this.automation.conditions[index].values = 'yes';
        return;
      }

      this.automation.conditions[index].filter_operator = this.automationTypes[
        this.automation.event_name
      ].conditions.find(
        condition => condition.key === currentCondition.attribute_key
      ).filterOperators[0].value;
      this.automation.conditions[index].values = '';
    },
    showUserInput(type, attributeKey) {
      if (attributeKey === 'meeting_present') return false;
      return !(type === 'is_present' || type === 'is_not_present');
    },
    hideOperatorForCondition(attributeKey) {
      return attributeKey === 'meeting_present';
    },
    showActionInput(action) {
      if (
        action === 'send_email_to_team' ||
        action === 'send_message' ||
        action === 'send_kivo_notification'
      )
        return false;
      const type = this.automationActionTypes.find(i => i.key === action)
        .inputType;
      return !!type;
    },
    resetAction(index) {
      const actionName = this.automation.actions[index].action_name;
      if (actionName === 'send_kivo_notification') {
        this.automation.actions[index].action_params = { url: '', text: '' };
        return;
      }
      this.automation.actions[index].action_params = [];
    },
    manifestConditions(automation) {
      const customAttributes = filterCustomAttributes(this.allCustomAttributes);
      const conditions = automation.conditions.map(condition => {
        const customAttr = isCustomAttribute(
          customAttributes,
          condition.attribute_key
        );
        let inputType = 'plain_text';
        if (customAttr) {
          inputType = getCustomAttributeInputType(customAttr.type);
        } else {
          inputType = getStandardAttributeInputType(
            this.automationTypes,
            automation.event_name,
            condition.attribute_key
          );
        }
        if (condition.attribute_key === 'meeting_present') {
          const isNo = condition.filter_operator === 'is_not_present';
          return {
            ...condition,
            query_operator: condition.query_operator || 'and',
            values: isNo ? 'no' : 'yes',
          };
        }
        if (
          condition.attribute_key === 'lead_quality_score' ||
          condition.attribute_key === 'reengagement_count'
        ) {
          const raw = Array.isArray(condition.values)
            ? condition.values[0]
            : condition.values;
          return {
            ...condition,
            query_operator: condition.query_operator || 'and',
            values: raw ?? '',
          };
        }
        if (inputType === 'plain_text' || inputType === 'date') {
          return {
            ...condition,
            query_operator: condition.query_operator || 'and',
            values: condition.values[0],
          };
        }
        return {
          ...condition,
          query_operator: condition.query_operator || 'and',
          values: [
            ...this.getConditionDropdownValues(condition.attribute_key),
          ].filter(item => [...condition.values].includes(item.id)),
        };
      });
      return conditions;
    },
    generateActionsArray(action) {
      const params = action.action_params;
      let actionParams = [];
      const inputType = this.automationActionTypes.find(
        item => item.key === action.action_name
      ).inputType;
      if (inputType === 'multi_select' || inputType === 'search_select') {
        const options = this.getActionDropdownValues(action.action_name);

        if (action.action_name === 'add_board') {
          const raw = Array.isArray(params) ? params[0] : params;
          let boardId;
          if (raw !== null && raw !== undefined && typeof raw === 'object') {
            boardId = raw.board_id ?? raw.id;
          } else {
            boardId = raw;
          }
          actionParams = options.filter(
            opt => boardId != null && String(opt.id) === String(boardId)
          );
        } else {
          // Default case: match item.id with each value in params (flat array)
          actionParams = options.filter(item => params.includes(item.id));
        }
      } else if (inputType === 'team_message') {
        actionParams = {
          team_ids: [
            ...this.getActionDropdownValues(action.action_name),
          ].filter(item => [...params[0].team_ids].includes(item.id)),
          message: params[0].message,
        };
      } else if (inputType === 'ai_generated_email') {
        const source = Array.isArray(params) ? params[0] : params;
        actionParams = {
          inbox_id: source?.inbox_id ?? null,
          macro_id: source?.macro_id ?? null,
        };
      } else if (inputType === 'kivo_webhook') {
        const source = Array.isArray(params) ? params[0] : params;
        actionParams = {
          url: source?.url ?? '',
          text: source?.text ?? '',
        };
      } else actionParams = [...params];
      return actionParams;
    },
    manifestActions(automation) {
      return automation.actions.map(action => {
        const p = action.action_params;
        const hasParams = Array.isArray(p)
          ? p.length > 0
          : p !== null &&
            p !== undefined &&
            p !== '' &&
            (typeof p !== 'object' || Object.keys(p).length > 0);

        let actionParams = [];
        if (hasParams) {
          actionParams = this.generateActionsArray(action);
        }

        const next = { ...action, action_params: actionParams };

        if (action.action_name === 'add_board' && actionParams.length) {
          const raw = Array.isArray(action.action_params)
            ? action.action_params[0]
            : action.action_params;
          const sid = raw?.status_id;
          if (sid != null) {
            const board = actionParams[0];
            const statuses =
              board?.statuses_attributes || board?.statuses || [];
            const st = statuses.find(s => String(s.id) === String(sid));
            if (st) next.status = st;
          }
        }

        return next;
      });
    },
    formatAutomation(automation) {
      this.automation = {
        ...automation,
        conditions: this.manifestConditions(automation),
        actions: this.manifestActions(automation),
      };
    },
    getActionDropdownValues(type) {
      const { agents, labels, teams, boards, macros} = this;
      return getActionOptions({ agents, labels, teams, languages, type , boards, macros});
    },
    manifestReengagementCountCondition() {
      Object.keys(this.automationTypes).forEach(eventKey => {
        const conditions = this.automationTypes[eventKey]?.conditions;
        if (!conditions) return;

        const exists = conditions.some(c => c.key === 'reengagement_count');
        if (!exists) {
          conditions.push({ ...REENGAGEMENT_COUNT_CONDITION });
        }
      });
    },
    manifestCustomAttributes() {
      const conversationCustomAttributesRaw = this.$store.getters[
        'attributes/getAttributesByModel'
      ]('conversation_attribute');

      const contactCustomAttributesRaw = this.$store.getters[
        'attributes/getAttributesByModel'
      ]('contact_attribute');
      const conversationCustomAttributeTypes = generateCustomAttributeTypes(
        conversationCustomAttributesRaw,
        'conversation_attribute'
      );
      const contactCustomAttributeTypes = generateCustomAttributeTypes(
        contactCustomAttributesRaw,
        'contact_attribute'
      );
      let manifestedCustomAttributes = generateCustomAttributes(
        conversationCustomAttributeTypes,
        contactCustomAttributeTypes,
        this.$t('AUTOMATION.CONDITION.CONVERSATION_CUSTOM_ATTR_LABEL'),
        this.$t('AUTOMATION.CONDITION.CONTACT_CUSTOM_ATTR_LABEL')
      );
      this.automationTypes.message_created.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.conversation_created.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.conversation_updated.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.conversation_opened.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.board_status_change.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.lead_quality_score_generated.conditions.push(
        ...manifestedCustomAttributes
      );
      this.automationTypes.contact_joined_meeting.conditions.push(
        ...manifestedCustomAttributes
      );
      //  this.automationTypes.contact_created.conditions.push(
      //   ...manifestedCustomAttributes
      // );
    },
  },
};
